import { FC, useState } from "react";
import { useParams } from "react-router-dom";

import { Typography } from "antd";
import { AnchorLinkItemProps } from "antd/es/anchor/Anchor";
import { DownloadOutlined } from "@ant-design/icons";
import { ActionButton } from "@hdi-seguros/web-components.action-button";
import { Card } from "@hdi-seguros/web-components.card";

import { FILE_TYPES, PreviousPDF } from "../../../../../shared/interfaces";
import {
  Anchor,
  ColSize,
  ErrorResults,
  RowSize,
} from "../../../../../shared/components";
import { _renderColumns } from "../../utils/renderSections";
import { COLORS } from "../../../../../config/themes/colors";
import { downloadBase64Files, downloadPresignedFile, formatDate } from "../../../../../shared/utils";

import "./index.css";
import DocumentsService from "../../../../../shared/services/documents";
import { EndorsementPolicyDocumentRequest } from "../../../../../shared/interfaces/services/get-endorsement-policy-document-service";
import { DocumentClass } from "../../../../../shared/interfaces/enums/document-class-enum";

interface PreviousPDFTabProps {
  previousPDFS?: Array<PreviousPDF>;
  showError?: boolean;
}

const MOCK_SECTIONS: Array<AnchorLinkItemProps> = [
  {
    key: "polizas-anteriores",
    href: "#polizas-anteriores",
    title: "Pólizas anteriores",
  },
];

export const PreviousPDFTab: FC<PreviousPDFTabProps> = ({
  previousPDFS = [],
  showError = false,
}) => {
  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isDownloadingMap, setIsDownloadingMap] = useState<Record<string, boolean>>({});

  setTimeout(() => setIsLoading(false), 1000);

  const downloadEndorsementPolicyPDF = async (fileName: string) => {
    try {
      setIsDownloadingMap((prev) => ({ ...prev, [fileName]: true }));
      const request: EndorsementPolicyDocumentRequest = {
        documento: {
          nombreDocumento: fileName,
          claseDocumental: DocumentClass.ElectronicPolicy,
          presigned: true
        },
      };
      const response = await DocumentsService.getEndorsementPolicyInPDF(
        request
      );
      downloadPresignedFile(response.data.documento, fileName, FILE_TYPES.PDF);
    } catch (error) {
    } finally {
      setIsDownloadingMap((prev) => ({ ...prev, [fileName]: false }));
    }
  };

  const generatePreviousPDFInfo = ({
    rama,
    poliza,
    suplementoPoliza,
    articulo,
    superpoliza,
    suplementoSuperpoliza,
    tipo,
    fechaEmision,
    vigenciaDesde,
    vigenciaHasta,
    nombreDocumento,
  }: PreviousPDF) => [
    {
      label: "Rama",
      value: rama,
    },
    {
      label: "Póliza",
      value: poliza,
    },
    {
      label: "Supl",
      value: suplementoPoliza,
    },
    {
      label: "Artículo",
      value: articulo,
    },
    {
      label: "Superpóliza",
      value: superpoliza,
    },
    {
      label: "SuplSpol",
      value: suplementoSuperpoliza,
    },
    {
      label: "Tipo",
      value: tipo,
    },
    {
      label: "Fecha Emisión",
      value: formatDate(fechaEmision),
    },
    {
      label: "Vigencia desde",
      value: formatDate(vigenciaDesde),
    },
    {
      label: "Vigencia hasta",
      value: formatDate(vigenciaHasta),
    },
    {
      label: "Documento",
      value: nombreDocumento?.length ? (
        <ActionButton
          type="secondary"
          label="Descargar"
          onClick={() => downloadEndorsementPolicyPDF(nombreDocumento)}
          loading={!!isDownloadingMap[nombreDocumento]}
          icon={<DownloadOutlined />}
        />
      ) : (
        "-"
      ),
    },
  ];
  return (
    <div className="previous-pdf-tab-container">
      {showError ? (
        <ErrorResults title="Hubo un error al intentar cargar los PDF´s previos de la póliza" />
      ) : (
        <RowSize rowProps={{ gutter: [0, 0] }}>
          <ColSize size={85} colProps={{ xs: { span: 21 }, sm: { span: 21 } }}>
            <Typography.Title level={ 4 } style={{ margin: 0, marginBottom: 10, marginTop: 20, color: COLORS.principal[100] }}
            >
              Pólizas anteriores
            </Typography.Title>

            <RowSize
              className="row-previous-pdf-tab"
              rowProps={{ gutter: [0, 0] }}
            >
              {previousPDFS?.length > 0 ? (
                previousPDFS.map((prevPdf, i) => (
                  <Card
                    title=""
                    key={i}
                    style={{ marginBottom: 15 }}
                    loading={isLoading}
                  >
                    <RowSize rowProps={{ gutter: [0, 10] }}>
                      {_renderColumns(generatePreviousPDFInfo(prevPdf))}
                    </RowSize>
                  </Card>
                ))
              ) : (
                <Typography.Text>
                  No existen pólizas anteriores a la {id}
                </Typography.Text>
              )}
            </RowSize>
          </ColSize>

          <ColSize
            size={9}
            colProps={{
              id: "previous-pdf-tab-container-anchor",
              xs: { span: 3 },
              sm: { span: 3 },
            }}
          >
            <Anchor
              items={MOCK_SECTIONS}
              containerId="previous-pdf-tab-container-anchor"
            />
          </ColSize>
        </RowSize>
      )}
    </div>
  );
};
